/**
 * Copy for the create-central-claim flow.
 *
 * Kept apart from `create-claim.ts` rather than merged into it: that file held the legacy claim
 * form's copy, and other screens still read it. The two word the same ideas differently.
 *
 * Mirrors `orders/create-order-rework.ts` key for key while the two flows are copies — see
 * `.claude/rules/app/orders-claims-parity.md`.
 */
export default {
  pageTitle: 'Create new claim',
  pageSubtitle: 'Claims',

  steps: {
    details: { label: 'Details', hint: 'Site & delivery' },
    inventory: { label: 'Inventory', hint: 'Add BOQ Items' },
    /** Claims-only — an order raises no deduction at creation, so its stepper has three segments. */
    contraCharges: { label: 'Contra charges', hint: 'Deduct from vendors' },
    /**
     * "Payments", not "Confirm": the step sets payment method, schedule, assignee and documentation
     * — it is where the payment details are entered, not a read-through of what came before. The
     * key stays `confirm` so every `data-cy`, form id and namespace is unchanged.
     */
    confirm: { label: 'Payments', hint: 'Method, schedule & docs' },
  },

  actions: {
    back: 'Back',
    cancel: 'Cancel',
    next: 'Next',
    /** Jumps Inventory straight to Payments, past the optional contra charges step. */
    skipToPayments: 'Skip to payments',
    /**
     * Context: "Request", not "Raise" — pressing this raises the request; the claim is only
     * *raised* once it is approved. Mirrors the order flow's twin.
     *
     * Plural, which it was not: the button is already handed a count (a claim per vendor), so a
     * two-vendor claim was reading "Request claim" with the count silently discarded.
     */
    requestClaim: 'Request claim | Request claims',
  },

  toast: {
    /** One claim request per vendor, so an inventory spanning several raises more than one. */
    raised: 'Claim raised | Claims raised',
    /**
     * A 2xx that carried no claims. Nothing was created, so the user has to be told — otherwise the
     * button simply stops spinning and the step sits there looking ignored.
     */
    notRaised: {
      title: 'Claim not raised',
      content: 'Nothing was created. Please try again.',
    },
    /**
     * The claim exists; only its deductions didn't land — retentions, contra charges, or both. Said
     * separately from the success so the user isn't sent back to raise a claim that is already
     * there, and named for the group rather than the kind so one toast covers either.
     */
    retentionsFailed: {
      title: "Deductions couldn't be added",
      content: 'The claim was raised. Add its deductions from the claim itself.',
    },
  },

  details: {
    section: {
      title: 'Project, site & delivery date',
      subtitle: "Claim items are pulled from this site's BOQ",
    },
    project: {
      label: 'Project',
      placeholder: 'Select a project',
      emptyMessage: 'No projects found',
    },
    site: {
      label: 'Site',
      placeholder: 'Select a site',
      emptyMessage: 'No sites with an approved BOQ',
    },
    deliveryDate: {
      label: 'Delivery date',
      placeholder: 'Select a date',
      /** Shown under the field once the site's range is known. */
      hint: 'Site dates: {range}',
    },
    note: {
      sectionTitle: 'Note',
      sectionSubtitle: 'Add an optional note to this claim',
      label: 'Note',
      placeholder: 'Add a note to this claim',
    },
    siteGuidance: {
      message:
        'Items are claimed against the selected site. To claim items not assigned to a specific site, create a site for General or Head Office purchases.',
      action: 'Create site',
    },
  },

  inventory: {
    title: 'Claim inventory',
    subtitle:
      "Search the site's BOQ to add items. Quantities over the available BOQ raise a variation.",
    clearAll: 'Clear all',
    /** The running count beside the title, e.g. "2 items across 2 vendors". */
    summary: '{items} across {vendors}',
    itemCount: '1 item | {count} items',
    vendorCount: '1 vendor | {count} vendors',

    /**
     * What the shared inventory components under `components/Inventory` can't say for themselves,
     * because it names the document being built. Everything else they show lives in the neutral
     * `inventory.*` namespace — see `translations/en/inventory.ts`.
     */
    table: {
      empty: {
        title: 'No items added yet',
        description: "Search the site's BOQ above to add the first line to this claim.",
      },
      /** The remaining-balance tooltip's row for what this claim is taking off the line. */
      selectedOnThisClaim: 'Selected on this claim',
    },

    totals: {
      claimTotal: 'Claim total (incl VAT)',
      projectedTotal: 'Claim total if approved (incl VAT)',
    },

    clearDialog: {
      title: 'Clear the claim inventory?',
      body: "Every line you've added will be removed, along with the quantities and rates set against them. The site's BOQ isn't affected, so you can add them again.",
      confirm: 'Clear 1 item | Clear all {count} items',
      cancel: 'Keep them',
    },
  },

  /**
   * The contra charges step. Its own namespace rather than reaching into `vendorPayments.*`: that
   * one is documented as the shared Confirm-step components' copy, and these controls are the
   * claim flow's alone — an order raises no contra charge at creation.
   */
  contraCharges: {
    title: 'Contra charges',
    subtitle:
      'Charge money back against a vendor before the claim is raised. Skip this step if there is nothing to deduct.',

    /** The step is optional, so the primary button says which of the two things it is doing. */
    skip: 'Skip',

    /** The band above each vendor's charges. */
    vendorClaimTotal: 'Claim total {amount}',

    table: {
      columns: {
        description: 'Charged back',
        item: 'Item',
        quantity: 'Qty',
        rate: 'Rate',
        amount: 'Amount',
      },
      /**
       * What each row is, as a tag — the same treatment the Confirm step gives the same charge, so
       * a reader meets one language for it across the two steps. A value contra charge has no name
       * of its own either way: the endpoint takes `{ amount }` and nothing else.
       */
      valueChargeLabel: 'Value contra charge',
      itemChargeLabel: 'Item contra charge',
      /** The card list's only: a card has no Qty or Rate column to put these figures in. */
      itemChargeSummary: '{quantity} {unit} × {rate}',
      /**
       * Whose BOQ line it is, which is not always the vendor being charged. Labelled rather than
       * left as a bare name, which would read as another fragment of the line's own description.
       * Shown on hover on the desktop table and inline on a card, which has no hover.
       */
      itemVendorSummary: 'BOQ line from {vendor}',
      none: 'No contra charges against this vendor',
      /** The expander names the rows it reveals rather than the gesture. */
      showCharges: 'Show contra charges for {vendor}',
      hideCharges: 'Hide contra charges for {vendor}',
    },

    actions: {
      /** Just "Add", as the Confirm step's own additions menu says it — the column names the rest. */
      add: 'Add',
      addValue: 'Value contra charge',
      addItem: 'Item contra charge',
      addValueDescription: 'Deduct a flat amount from what this vendor is paid',
      addItemDescription: 'Charge back a BOQ line at a quantity and a rate',
      /**
       * Names the edit control, which is a bare icon — so this is the only name it gets, and it
       * says *what* is being edited rather than repeating the gesture.
       */
      editLabel: 'Edit item contra charge',
      remove: 'Remove contra charge',
    },

    /** The step's own summary, across every vendor. */
    totals: {
      claimTotal: 'Claim total (incl VAT)',
      chargesTotal: 'Contra charges',
      revisedTotal: 'Revised claim total',
    },

    empty: {
      title: 'No vendors to charge back',
      description: 'Add lines on the Inventory step first — a contra charge comes off a vendor.',
    },
  },

  confirm: {
    title: 'Payment details',

    /**
     * What the vendor's total is net of, named in its breakdown. A value contra charge has no name
     * of its own — the endpoint takes `{ amount }` and nothing else — so it is named by kind; an
     * item one is named by its line, which is what tells two of them apart.
     */
    deductions: {
      valueContraCharge: 'Value contra charge',
    },
    subtitle:
      'One claim request per vendor. Set payment, an optional deposit or retention, and documentation on each.',

    /**
     * What the shared vendor-payments components under `components/VendorPayments` can't say for
     * themselves, because it names the document being built. Everything else they show lives in the
     * neutral `vendorPayments.*` namespace — see `translations/en/vendor-payments.ts`.
     */
    requestType: { claim: 'Claim' },

    documentation: {
      title: 'Claim documentation',
      infoMessage:
        'These documents are optional at creation. You can also upload them later from the claim detail page on an individual claim.',
      accordionTitle: 'Key documentation',
      accordionSubtitle:
        'All documents required for the completion of this claim and its associated payments.',
      prerequisitesTitle: 'Claim prerequisites',
      quoteLabel: 'Quote/Proforma Invoice',
      invoiceLabel: 'Tax Invoice',
      fileHint: 'PDF, JPG or PNG · up to 10MB per file · Multiple files supported',
      done: 'Done',
    },

    emptyTitle: 'Nothing to confirm yet',
    emptyDescription: 'Add items on the Inventory step and the vendors to pay will appear here.',

    table: {
      columns: {
        /**
         * Heads the column holding a deposit's or a retention's share. "Additions" rather than
         * naming both: it is what the legacy claim form called them, and it keeps the heading from
         * growing every time the flow learns a new kind.
         */
        deposit: 'Additions',
      },
      tooltips: {
        method:
          "Sets this claim's payment priority. Cash on delivery is paid before the due date, account payments when the vendor account is settled, and a deposit before the delivery date.",
        deposit:
          'Splits the approved claim into separate payment requests: a deposit paid up front, a retention held back, and one for the balance.',
      },
    },
  },

  deductions: {
    title: 'Deductions',
    subtitle: 'Charge BOQ items back against this claim. These come off what the vendor is paid.',
    searchPlaceholder: 'Search for an item to charge back',
  },

  clearInventoryDialog: {
    title: 'Change the {subject}?',
    body: "The items on this claim come from the current site's BOQ, so changing the {subject} removes every line you've added. You'll need to add the items again against the new site.",
    confirm: 'Change and clear',
    cancel: 'Keep what I have',
    subject: { project: 'project', site: 'site' },
  },

  leaveDialog: {
    title: 'Discard this claim?',
    body: "Your progress isn't saved anywhere. Leaving now discards what you've entered and you'll need to start this claim again.",
    confirm: 'Discard claim',
    cancel: 'Keep editing',
  },

  deliveryDateDialog: {
    title: 'Delivery date is outside the site dates',
    body: "The delivery date you've picked ({date}) falls outside this site's dates of {range}.",
    /**
     * Stops at "when it's approved" deliberately: the API extends the range to cover the approval
     * date, not the claim's date, so naming the resulting dates would promise what it doesn't do.
     */
    note: "You can still use it. The site's dates are extended to cover this claim when it's approved, and any linked draws move with them.",
    confirm: 'Use this date',
    cancel: 'Pick another date',
  },
};
